import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageParts";
import { ExampleDownloads } from "@/components/import/ExampleDownloads";

const EMPTY = { name: "", kind: "keyword", definition: "", reminder_text: "", keywords: "" };

function ProposeForm({ onDone }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/mechanics", { ...f, keywords: f.keywords.split(",").map((k) => k.trim()).filter(Boolean) });
      toast.success("Mechanic submitted for independent review");
      setF(EMPTY);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form className="panel grid gap-2 p-5" onSubmit={submit} data-testid="mechanic-propose-form">
      <p className="eyebrow">Propose a mechanic</p>
      <div className="grid gap-2 sm:grid-cols-[1fr_180px]">
        <input className="field" placeholder="Name" value={f.name} onChange={set("name")} data-testid="mechanic-name-input" />
        <select className="field" value={f.kind} onChange={set("kind")} data-testid="mechanic-kind-select">
          <option value="keyword">Keyword</option><option value="ability_word">Ability word</option><option value="mechanic">Mechanic</option></select>
      </div>
      <textarea rows={3} className="field" placeholder="Definition (rules text)" value={f.definition} onChange={set("definition")} data-testid="mechanic-definition-input" />
      <input className="field" placeholder="Reminder text" value={f.reminder_text} onChange={set("reminder_text")} data-testid="mechanic-reminder-input" />
      <input className="field" placeholder="Related keywords, comma separated" value={f.keywords} onChange={set("keywords")} data-testid="mechanic-keywords-input" />
      <button className="btn-gold justify-self-start" disabled={f.name.length < 2 || f.definition.length < 10} data-testid="mechanic-submit-button">Submit for review</button>
    </form>
  );
}

export default function Mechanics() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const params = { q: q || undefined, mine: mine || undefined };
  const { data } = useQuery({ queryKey: ["mechanics", params], queryFn: () => api.get("/mechanics", { params }).then((r) => r.data) });
  return (
    <div className="container-x py-12" data-testid="mechanics-page">
      <PageHeader eyebrow="Rules library" title="Custom mechanics" testid="mechanics-header"
        description="Community-made keywords and mechanics. Platform-wide entries are approved by two independent Rules Reviewers; community mechanics by that community's staff." />
      <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
        <div>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input className="field pl-9" placeholder="Search name, definition or reminder text" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mechanics-search-input" /></div>
            {user && <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="mechanics-mine-toggle" />My proposals</label>}
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {data?.items.map((m) => (
              <li key={m.mechanic_id}><Link to={`/mechanics/${m.mechanic_id}`} className="panel block p-4 transition-colors hover:border-amber-500/60" data-testid={`mechanic-card-${m.mechanic_id}`}>
                <div className="flex items-center justify-between gap-2"><h3 className="font-heading text-amber-100">{m.name}</h3>
                  <span className="font-mono text-[10px] uppercase text-slate-500">{m.status === "approved" ? `v${m.version}` : m.status.replace("_", " ")}</span></div>
                {m.reminder_text && <p className="mt-1 text-xs italic text-slate-400">({m.reminder_text})</p>}
                <p className="mt-2 line-clamp-2 text-sm text-slate-300">{m.definition}</p>
                <p className="mt-2 font-mono text-[10px] text-slate-500">@{m.creator?.username}{m.community ? ` · ${m.community.name}` : " · platform-wide"}</p>
              </Link></li>
            ))}
          </ul>
          {data && !data.items.length && <p className="text-sm text-slate-500" data-testid="mechanics-empty">No mechanics found.</p>}
        </div>
        {user ? <ProposeForm onDone={() => qc.invalidateQueries({ queryKey: ["mechanics"] })} /> : <p className="text-sm text-slate-500">Sign in to propose a mechanic.</p>}
      </div>
      <div className="mt-10"><ExampleDownloads testid="mechanics-examples" showImporterLink /></div>
    </div>
  );
}
